import { useQuery, useQueryClient } from '@tanstack/react-query'
import { commissionApi } from '@/api/commissions'
import type {
  CommissionListParams,
  DepartmentCommissionListParams,
} from '@/types/commission'

// Query Keys
export const commissionKeys = {
  all: ['commissions'] as const,
  myLists: () => [...commissionKeys.all, 'my'] as const,
  myList: (params: CommissionListParams) => [...commissionKeys.myLists(), params] as const,
  departmentLists: () => [...commissionKeys.all, 'department'] as const,
  departmentList: (params: DepartmentCommissionListParams) =>
    [...commissionKeys.departmentLists(), params] as const,
}

/**
 * 获取我的佣金列表
 */
export function useMyCommissions(params: CommissionListParams) {
  return useQuery({
    queryKey: commissionKeys.myList(params),
    queryFn: () => commissionApi.getMyCommissions(params),
    placeholderData: (previousData) => previousData,
  })
}

/**
 * 获取部门佣金列表
 */
export function useDepartmentCommissions(params: DepartmentCommissionListParams) {
  return useQuery({
    queryKey: commissionKeys.departmentList(params),
    queryFn: () => commissionApi.getDepartmentCommissions(params),
    placeholderData: (previousData) => previousData,
  })
}

/**
 * 刷新佣金列表
 */
export function useCommissionListRefresh() {
  const queryClient = useQueryClient()

  const refreshMyCommissions = () => {
    queryClient.invalidateQueries({ queryKey: commissionKeys.myLists() })
  }

  const refreshDepartmentCommissions = () => {
    queryClient.invalidateQueries({ queryKey: commissionKeys.departmentLists() })
  }

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: commissionKeys.all })
  }

  return {
    refreshMyCommissions,
    refreshDepartmentCommissions,
    refreshAll,
  }
}
